import React, { useEffect, useState } from 'react';
import PeriodBar, { usePeriod } from '../components/PeriodBar';
import Notice from '../components/Notice';

export default function StudentAttendance() {
  const [allClasses, setClasses] = useState([]);
  // 授業の欄は今の期間の授業だけ（ほかの学期は「ほかの期間」から）【2026-09-17】
  const period = usePeriod({ lockYear: true });
  const classes = period.filter(allClasses);
  const [classId, setClassId] = useState('');
  const [start,   setStart]   = useState('');
  const [end,     setEnd]     = useState('');
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState('');

  // 初回：履修授業をdropdown用に取得
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/dashboard/student', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(d => { if (d.success) setClasses(d.dashboard.classes || []); });
  }, []);

  const search = async () => {
    setLoading(true); setErr(''); setRecords(null);
    try {
      const token  = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (classId) params.append('classId', classId);
      params.append('limit', '500');
      // X-App-Version を付けると、学生に見せない列（不正検知の印そのもの・スコア・端末ID）を落とした形で返る【2026-11-02】
      const res    = await fetch('/api/attendance/history?' + params.toString(),
                                 { headers: { Authorization: 'Bearer ' + token, 'X-App-Version': 'web' } });
      const data   = await res.json();
      if (!data.success) throw new Error(data.message || '取得に失敗');

      let rows = data.history || [];
      if (start) rows = rows.filter(r => r.attendance_date >= start);
      if (end)   rows = rows.filter(r => r.attendance_date <= end);
      setRecords(rows);
    } catch (e) { setErr(e.message); }
    finally     { setLoading(false); }
  };

  const reset = () => {
    setClassId(''); setStart(''); setEnd(''); setRecords(null); setErr('');
  };

  const total   = records?.length || 0;
  const present = records?.filter(r => r.status === 'present').length || 0;
  const late    = records?.filter(r => r.status === 'late').length    || 0;
  const absent  = records?.filter(r => r.status === 'absent').length  || 0;
  const rate    = total > 0 ? (((present + late) / total) * 100).toFixed(1) : null;
  // 先生が「学生にも印を見せる」にしている授業があるときだけ、注意の列を出す【2026-11-02】
  const anyShared = !!records?.some(r => r.flagsShared);
  const anyNotice = !!records?.some(r => (r.notices || []).length > 0);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>出席確認</h1>
        <p>過去の出席記録を授業・期間で絞り込んで確認できます。</p>
      </div>

      {/* 検索フォーム */}
      <div className="card">
        <div className="card-header"><h2>絞り込み</h2></div>

        <div className="form-row">
          <label>授業</label>
          <PeriodBar period={period} />
          <select value={classId} onChange={e => setClassId(e.target.value)}>
            <option value="">— この期間のすべての授業 —</option>
            {classes.map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.semester ? ' (' + c.semester + ')' : ''}</option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <label>期間</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="date" value={start} onChange={e => setStart(e.target.value)}/>
            <span>〜</span>
            <input type="date" value={end}   onChange={e => setEnd(e.target.value)}/>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button className="btn-primary" onClick={search} disabled={loading}>
            {loading ? '検索中...' : '検索'}
          </button>
          <button className="btn-secondary" onClick={reset}>クリア</button>
        </div>
      </div>

      {/* エラー */}
      <Notice kind="error">{err}</Notice>

      {/* サマリ */}
      {records !== null && total > 0 && (
        <div className="stats-grid">
          <SmallStat label="該当件数" value={total}    color="#3b82f6"/>
          <SmallStat label="出席"     value={present}  color="#22c55e"/>
          <SmallStat label="遅刻"     value={late}     color="#f59e0b"/>
          <SmallStat label="欠席"     value={absent}   color="#ef4444"/>
        </div>
      )}

      {records !== null && total > 0 && (
        <div className="card" style={{ padding: '0.85rem 1.25rem' }}>
          <p>出席率（出席+遅刻 / 全体）: <strong style={{ fontSize: '1.1rem' }}>{rate}%</strong></p>
        </div>
      )}

      {anyNotice && (
        <Notice kind="info">
          先生が確認のために見ている、出席の記録についての注意です。電波や設定の都合で付くこともあります。
          心当たりが無いときや、事情があるときは、先生に相談してください。
        </Notice>
      )}

      {/* 一覧 */}
      <div className="card">
        <div className="card-header"><h2>出席記録一覧</h2></div>
        {records === null ? (
          <EmptyState text="条件を選んで検索ボタンを押してください"/>
        ) : total === 0 ? (
          <EmptyState text="該当する出席記録がありません"/>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>日付</th><th>時刻</th><th>授業</th><th>教室</th><th>方法</th><th>状態</th>{anyShared && <th>記録についての注意</th>}</tr>
              </thead>
              <tbody>
                {records.map((r, i) => (
                  <tr key={i}>
                    <td>{r.attendance_date}</td>
                    <td>{formatTime(r.timestamp)}</td>
                    <td><strong>{r.class_name || '-'}</strong></td>
                    <td>{r.location || '-'}</td>
                    <td>{r.device_type || '—'}</td>
                    <td><StatusBadge status={r.status}/></td>
                    {anyShared && (
                      <td style={{ fontSize: '0.82rem' }}>
                        {(r.notices || []).length === 0 ? <span style={{ color: '#9ca3af' }}>{r.flagsShared ? 'なし' : '—'}</span>
                          : r.notices.map(n => (
                            <div key={n.type} style={{ color: NOTICE_COLOR[n.severity] || '#4b5563' }}>⚠ {n.text}</div>
                          ))}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// 注意の文の色（重いほど赤）【2026-11-02】
const NOTICE_COLOR = { critical: '#b91c1c', high: '#c2410c', warn: '#a16207', info: '#4b5563' };

function SmallStat({ label, value, color }) {
  return (
    <div className="stat-card" style={{ borderTop: '4px solid ' + color }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    present: { text: '出席', bg: '#dcfce7', fg: '#166534' },
    late:    { text: '遅刻', bg: '#fef3c7', fg: '#92400e' },
    absent:  { text: '欠席', bg: '#fee2e2', fg: '#991b1b' },
  };
  const s = map[status] || { text: status, bg: '#e5e7eb', fg: '#374151' };
  return (
    <span style={{
      display: 'inline-block', padding: '0.2rem 0.7rem', borderRadius: '12px',
      background: s.bg, color: s.fg, fontSize: '0.8rem', fontWeight: 600
    }}>{s.text}</span>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#888' }}>
      {icon ? <div style={{ marginBottom: '0.5rem', color: '#c7ccd4' }}>{icon}</div> : null}
      <p>{text}</p>
    </div>
  );
}

function formatTime(ts) {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
}
